/*
  FanScore Core
  Shared browser runtime for all FanScore portals.

  Responsibilities:
  - Supabase auth/session
  - Role detection
  - Portal routing
  - School context
  - Athlete context
  - Portal preference persistence
  - Team theming
  - Portal analytics

  Shopify asset filename:
  assets/fanscore-core.js
*/
(function(global){
  'use strict';

  if (global.FanScoreCore) return;

  var CONFIG = {
    supabaseUrl: 'https://niqkkbqqmmjxldjeyhdp.supabase.co',
    publishableKey: 'sb_publishable_2lnFh7hCJdy5717GIUZLWQ_xQMZWLPK',
    storageKey: 'fanscore-hub-auth',
    gatewayUrl: '/pages/fanscore'
  };

  var state = {
    client: null,
    session: null,
    context: null,
    contextLoadedAt: 0,
    readyPromise: null,
    sessionKey: null
  };

  function uuid(){
    if (global.crypto && typeof global.crypto.randomUUID === 'function') {
      return global.crypto.randomUUID();
    }
    return 'fs-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  function now(){
    return Date.now();
  }

  function currentPath(){
    try {
      return global.location.pathname + global.location.search;
    } catch (_e) {
      return '';
    }
  }

  function loadSupabase(){
    return new Promise(function(resolve,reject){
      if (global.supabase && global.supabase.createClient) {
        resolve(global.supabase);
        return;
      }

      var existing = document.querySelector('script[data-fanscore-supabase]');
      if (existing) {
        existing.addEventListener('load', function(){ resolve(global.supabase); }, { once:true });
        existing.addEventListener('error', reject, { once:true });
        return;
      }

      var script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2';
      script.async = true;
      script.setAttribute('data-fanscore-supabase','true');
      script.onload = function(){ resolve(global.supabase); };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async function getClient(){
    if (state.client) return state.client;

    await loadSupabase();

    state.client = global.supabase.createClient(
      CONFIG.supabaseUrl,
      CONFIG.publishableKey,
      {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          storageKey: CONFIG.storageKey
        }
      }
    );

    state.client.auth.onAuthStateChange(function(_event, session){
      state.session = session;
      if (!session) {
        state.context = null;
        state.contextLoadedAt = 0;
      }
    });

    return state.client;
  }

  async function ready(){
    if (state.readyPromise) return state.readyPromise;

    state.readyPromise = (async function(){
      var client = await getClient();
      var result = await client.auth.getSession();
      if (result.error) throw result.error;
      state.session = result.data.session;

      try {
        state.sessionKey = global.sessionStorage.getItem('fanscore-session-key');
      } catch (_e) {}

      if (!state.sessionKey) {
        state.sessionKey = uuid();
        try {
          global.sessionStorage.setItem('fanscore-session-key',state.sessionKey);
        } catch (_e) {}
      }

      return {
        client: client,
        session: state.session
      };
    })();

    return state.readyPromise;
  }

  async function getSession(){
    await ready();
    return state.session;
  }

  async function signIn(email,password){
    var client = await getClient();
    var result = await client.auth.signInWithPassword({
      email: String(email || '').trim(),
      password: password || ''
    });
    if (result.error) throw result.error;
    state.session = result.data.session;
    await refreshContext(true);
    return result.data;
  }

  async function magicLink(email,redirectTo){
    var client = await getClient();
    var result = await client.auth.signInWithOtp({
      email: String(email || '').trim(),
      options: {
        emailRedirectTo: redirectTo || global.location.href
      }
    });
    if (result.error) throw result.error;
    return result.data;
  }

  async function signOut(){
    var client = await getClient();
    var result = await client.auth.signOut();
    if (result.error) throw result.error;
    state.session = null;
    state.context = null;
    state.contextLoadedAt = 0;
    return true;
  }

  async function refreshContext(force){
    await ready();
    if (!state.session) return null;

    if (!force && state.context && (now() - state.contextLoadedAt) < 15000) {
      return state.context;
    }

    var result = await state.client.rpc('fs_core_context');
    if (result.error) throw result.error;
    state.context = result.data || {};
    state.contextLoadedAt = now();
    return state.context;
  }

  async function getContext(force){
    return refreshContext(Boolean(force));
  }

  function portalRecord(name,context){
    var ctx = context || state.context || {};
    var portals = Array.isArray(ctx.portals) ? ctx.portals : [];
    return portals.find(function(p){ return p.portal === name; }) || null;
  }

  function canAccess(name,context){
    var p = portalRecord(name,context);
    return Boolean(p && p.allowed);
  }

  function getRoute(name,context){
    var p = portalRecord(name,context);
    return p && p.url ? p.url : null;
  }

  function allowedPortals(context){
    var ctx = context || state.context || {};
    var portals = Array.isArray(ctx.portals) ? ctx.portals : [];
    return portals.filter(function(p){ return Boolean(p.allowed); });
  }

  function selectedSchool(context){
    var ctx = context || state.context || {};
    var schools = Array.isArray(ctx.schools) ? ctx.schools : [];
    return schools.find(function(s){ return s.id === ctx.selected_school_id; }) || schools[0] || null;
  }

  function selectedAthlete(context){
    var ctx = context || state.context || {};
    var athletes = Array.isArray(ctx.athletes) ? ctx.athletes : [];
    return athletes.find(function(a){ return a.id === ctx.selected_athlete_id; }) || athletes[0] || null;
  }

  async function setContext(options){
    options = options || {};
    await ready();
    if (!state.session) throw new Error('Authentication required');

    var result = await state.client.rpc('fs_core_set_context',{
      p_portal: options.portal || null,
      p_school_id: options.schoolId || null,
      p_athlete_id: options.athleteId || null,
      p_set_default: Boolean(options.setDefault)
    });

    if (result.error) throw result.error;
    state.context = result.data || {};
    state.contextLoadedAt = now();
    return state.context;
  }

  async function track(portal,eventType,options){
    options = options || {};
    await ready();
    if (!state.session) return null;

    try {
      var result = await state.client.rpc('fs_core_track_event',{
        p_portal: portal,
        p_event_type: eventType,
        p_school_id: options.schoolId || (state.context && state.context.selected_school_id) || null,
        p_athlete_id: options.athleteId || (state.context && state.context.selected_athlete_id) || null,
        p_page_path: options.pagePath || currentPath(),
        p_session_key: state.sessionKey,
        p_metadata: options.metadata || {}
      });
      if (result.error) throw result.error;
      return result.data;
    } catch (error) {
      if (global.console && console.warn) {
        console.warn('FanScore analytics event failed:',error);
      }
      return null;
    }
  }

  async function enterPortal(name,options){
    options = options || {};
    var session = await getSession();

    if (!session) {
      return {
        authenticated: false,
        allowed: false,
        redirect: CONFIG.gatewayUrl
      };
    }

    var context = await getContext(Boolean(options.forceContext));

    if (!canAccess(name,context)) {
      if (options.redirectOnDenied !== false && global.location && !global.__FANSCORE_V2_PAGE__) {
        global.location.assign(options.gatewayUrl || CONFIG.gatewayUrl);
      }
      return {
        authenticated: true,
        allowed: false,
        redirect: options.gatewayUrl || CONFIG.gatewayUrl,
        context: context
      };
    }

    var schoolId = options.schoolId || context.selected_school_id || null;
    var athleteId = options.athleteId || context.selected_athlete_id || null;

    context = await setContext({
      portal: name,
      schoolId: schoolId,
      athleteId: athleteId,
      setDefault: false
    });

    if (options.track !== false) {
      track(name,'portal_view',{
        schoolId: context.selected_school_id || null,
        athleteId: context.selected_athlete_id || null,
        metadata: {
          role_count: context.role_count || 0
        }
      });
    }

    return {
      authenticated: true,
      allowed: true,
      context: context
    };
  }

  async function autoRoute(options){
    options = options || {};
    var session = await getSession();

    if (!session) {
      return {
        authenticated: false,
        routed: false,
        reason: 'not_authenticated'
      };
    }

    var context = await getContext(Boolean(options.forceContext));
    var allowed = allowedPortals(context);
    var currentPortal = options.currentPortal || null;

    if (!allowed.length) {
      return {
        authenticated: true,
        routed: false,
        reason: 'no_portal_access',
        context: context
      };
    }

    if (allowed.length === 1 && options.singleRoleRedirect !== false) {
      var only = allowed[0];
      if (currentPortal !== only.portal && only.url && !global.__FANSCORE_V2_PAGE__) {
        await track('gateway','single_role_redirect',{
          metadata:{ target_portal:only.portal }
        });
        global.location.assign(only.url);
        return {
          authenticated: true,
          routed: true,
          target: only.portal,
          url: only.url,
          context: context
        };
      }
    }

    if (options.useDefault === true) {
      var target = allowed.find(function(p){ return p.portal === context.default_portal; }) || allowed[0];
      if (target && currentPortal !== target.portal && target.url && !global.__FANSCORE_V2_PAGE__) {
        await track('gateway','default_portal_redirect',{
          metadata:{ target_portal:target.portal }
        });
        global.location.assign(target.url);
        return {
          authenticated: true,
          routed: true,
          target: target.portal,
          url: target.url,
          context: context
        };
      }
    }

    return {
      authenticated: true,
      routed: false,
      reason: allowed.length > 1 ? 'multiple_roles' : 'already_on_portal',
      context: context
    };
  }

  async function choosePortal(name,options){
    options = options || {};
    var context = await getContext(true);
    if (!canAccess(name,context)) throw new Error('Portal access denied');

    var portal = portalRecord(name,context);
    context = await setContext({
      portal: name,
      schoolId: options.schoolId || context.selected_school_id || null,
      athleteId: options.athleteId || context.selected_athlete_id || null,
      setDefault: Boolean(options.setDefault)
    });

    await track('gateway','portal_selected',{
      schoolId: context.selected_school_id || null,
      athleteId: context.selected_athlete_id || null,
      metadata:{
        selected_portal:name,
        set_default:Boolean(options.setDefault)
      }
    });

    if (options.navigate !== false && portal && portal.url) {
      global.location.assign(portal.url);
    }

    return context;
  }

  function applySchoolTheme(root,school){
    if (!root || !school || !root.style) return;
    root.style.setProperty('--team-primary',school.primary_color || '#101722');
    root.style.setProperty('--team-secondary',school.secondary_color || '#d7a93a');
    root.style.setProperty('--team-accent',school.accent_color || school.secondary_color || '#d7a93a');
    root.style.setProperty('--primary',school.primary_color || '#101722');
    root.style.setProperty('--secondary',school.secondary_color || '#d7a93a');
    root.style.setProperty('--accent',school.accent_color || school.secondary_color || '#d7a93a');
  }

  async function portalData(name){
    await enterPortal(name,{ redirectOnDenied:false, track:false });
    var rpcMap = {
      fan: 'fs_fan_portal_context',
      athlete: 'fs_athlete_portal_context',
      parent: 'fs_parent_portal_context',
      agent: 'fs_agent_portal_context',
      admin: 'fs_admin_context_v2'
    };

    var rpc = rpcMap[name];
    if (!rpc) throw new Error('Unknown portal');

    var result = await state.client.rpc(rpc);
    if (result.error) throw result.error;
    return result.data || {};
  }

  async function requirePortal(name,options){
    var result = await enterPortal(name,options || {});
    if (!result.allowed) {
      throw new Error('This account does not have access to the ' + name + ' portal.');
    }
    return result.context;
  }

  global.FanScoreCore = {
    version: '1.0.0',
    config: CONFIG,
    ready: ready,
    getClient: getClient,
    getSession: getSession,
    signIn: signIn,
    magicLink: magicLink,
    signOut: signOut,
    getContext: getContext,
    refreshContext: refreshContext,
    canAccess: canAccess,
    getRoute: getRoute,
    allowedPortals: allowedPortals,
    selectedSchool: selectedSchool,
    selectedAthlete: selectedAthlete,
    setContext: setContext,
    track: track,
    enterPortal: enterPortal,
    requirePortal: requirePortal,
    autoRoute: autoRoute,
    choosePortal: choosePortal,
    applySchoolTheme: applySchoolTheme,
    portalData: portalData
  };
})(window);
